import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TrendingUp, UserMinus, PlusCircle, Calendar } from "lucide-react";

const BASE = process.env.REACT_APP_API_URL || "";

// Fetch real dashboard metrics from backend
async function fetchMetrics() {
  const res = await fetch(`${BASE}/metrics/dashboard`, {
    credentials: 'include', // Use httpOnly cookie
  });
  if (!res.ok) throw new Error("Failed to fetch metrics");
  const json = await res.json();
  return json.data;
}

const MetricCards = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const data = await fetchMetrics();
        setMetrics(data);
      } catch (err) {
        console.error("Failed to load metrics:", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-xl sm:rounded-2xl shadow p-3 sm:p-5 flex flex-col items-center text-center border-t-4 border-gray-300 animate-pulse">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded mb-2"></div>
            <div className="w-12 sm:w-16 h-5 sm:h-6 bg-gray-200 rounded mb-1"></div>
            <div className="w-16 sm:w-20 h-3 sm:h-4 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    {
      icon: <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-[#172A3A]" />,
      label: t('retentionRate'),
      value: metrics?.retentionRate || "0%",
      shortValue: metrics?.retentionRate || "0%",
      path: '/clients',
    },
    {
      icon: <UserMinus className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />,
      label: t('churnedClients'),
      value: metrics?.churned ? `${metrics.churned} (12 mo)` : `0 (12 mo)`,
      shortValue: metrics?.churned?.toString() || "0",
      path: '/clients?status=churned',
    },
    {
      icon: <PlusCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />,
      label: t('newClients'),
      value: metrics?.newClients ? `${metrics.newClients} (${t('thisMonth')})` : `0 (${t('thisMonth')})`,
      shortValue: metrics?.newClients?.toString() || "0",
      path: '/clients?filter=new_this_month',
    },
    {
      icon: <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-[#007cf0]" />,
      label: t('tasksDue'),
      value: metrics?.tasksDue?.toString() || "0",
      shortValue: metrics?.tasksDue?.toString() || "0",
      path: '/tasks?filter=due',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
      {cards.map(({ icon, label, value, shortValue, path }) => (
        <div
          key={label}
          onClick={() => navigate(path)}
          className="bg-white rounded-xl sm:rounded-2xl shadow p-3 sm:p-5 flex flex-col items-center text-center border-t-4 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
          style={{ borderTopColor: label === "Retention Rate" ? "#FFB800" : "#20344A" }}
        >
          <div className="mb-1 sm:mb-2">{icon}</div>
          <div className="text-lg sm:text-2xl font-bold text-[#172A3A]">
            <span className="sm:hidden">{shortValue}</span>
            <span className="hidden sm:inline">{value}</span>
          </div>
          <div className="text-xs sm:text-sm text-[#20344A] leading-tight">{label}</div>
        </div>
      ))}
    </div>
  );
};

export default MetricCards;
